import {
	faBolt,
	faBrain,
	faClock,
	faCodeBranch,
	faCodeCompare,
	faCommentSms,
	faDownload,
	faEnvelope,
	faGlobePointer,
	faInbox,
	faPhone,
	faSearch,
	faUsersGear,
} from '@fortawesome/pro-solid-svg-icons';
import { Capabilities, CapabilityService } from '@supply-mover-2-0/shared';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../components/base/Icon';
import { Modal } from '../../components/overlays/Modal';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import type { FlowStep } from '../types/automation';

interface StepSelectorModalProps {
	visible: boolean;
	position?: { x: number; y: number };
	onSelect: (stepType: FlowStep['stepType']) => void;
	onCancel: () => void;
}

const StepSelectorModal: React.FC<StepSelectorModalProps> = ({
	visible,
	onSelect,
	onCancel,
}) => {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [searchTerm, setSearchTerm] = useState('');
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const searchInputRef = useRef<HTMLInputElement>(null);

	// Static step types with UI handlers - no need to load from API every time
	const availableStepTypes = useMemo(
		() => [
			{
				type: 'send_email' as const,
				label: 'Send Email',
				description: 'Send an email to contacts',
				icon: faEnvelope,
				color: colors.values.primaryDark,
				keywords: ['email', 'mail', 'message', 'send'],
			},
			{
				type: 'send_sms' as const,
				label: 'Send SMS',
				description: 'Send a text message to a phone number',
				icon: faCommentSms,
				color: colors.values.info,
				keywords: ['sms', 'text', 'message', 'phone'],
			},
			{
				type: 'delay' as const,
				label: 'Wait',
				description: 'Wait for a specific time',
				icon: faClock,
				color: colors.values.secondary,
				keywords: ['wait', 'delay', 'pause', 'time', 'timer'],
			},
			{
				type: 'wait_for_event' as const,
				label: 'Wait for Event',
				description: 'Wait for a specific event to occur',
				icon: faBolt,
				color: colors.values.info,
				keywords: ['wait', 'event', 'trigger', 'listen'],
			},
			{
				type: 'condition' as const,
				label: 'Condition',
				description: 'Branch based on behavior',
				icon: faCodeBranch,
				color: colors.values.warning,
				keywords: ['condition', 'if', 'branch', 'logic', 'decision'],
			},
			{
				type: 'parallel' as const,
				label: 'Parallel Branches',
				description: 'Execute multiple flow branches in parallel',
				icon: faCodeBranch,
				color: colors.values.info,
				keywords: ['parallel', 'branch', 'split', 'concurrent', 'multiple'],
			},
			{
				type: 'agent_call' as const,
				label: 'Outbound Agent Call',
				description: 'Make an outbound call with AI agent',
				icon: faPhone,
				color: colors.values.success,
				keywords: ['call', 'phone', 'agent', 'ai', 'outbound', 'voice'],
			},
			{
				type: 'evaluate' as const,
				label: 'Process with AI',
				description:
					'Use an LLM with tools to process data and perform actions',
				icon: faBrain,
				color: colors.values.warning,
				keywords: ['ai', 'llm', 'evaluate', 'process', 'gpt', 'claude', 'tool'],
			},
			{
				type: 'email_ingest' as const,
				label: 'Read Email',
				description: 'Read an email and its attachments into structured data',
				icon: faInbox,
				color: colors.values.primary,
				keywords: [
					'email',
					'read',
					'inbound',
					'message',
					'attachment',
					'extract',
					'parse',
					'pdf',
					'spreadsheet',
					'image',
				],
			},
			{
				type: 'product_match' as const,
				label: 'Product Match',
				description:
					'Match extracted product lines against the item catalog and queue results for review',
				icon: faCodeCompare,
				color: colors.values.info,
				keywords: [
					'match',
					'product',
					'catalog',
					'cross reference',
					'quote',
					'item',
					'sku',
				],
			},
			{
				type: 'external_lookup' as const,
				label: 'External Lookup',
				description:
					'Look up product facts on the web for lines that did not match',
				icon: faGlobePointer,
				color: colors.values.info,
				keywords: [
					'external',
					'lookup',
					'enrich',
					'enrichment',
					'web',
					'search',
					'source',
					'datasheet',
					'manufacturer',
					'no match',
				],
			},
			{
				type: 'load_data' as const,
				label: 'Load Data',
				description: 'Load entity data for use in subsequent steps',
				icon: faDownload,
				color: colors.values.info,
				keywords: ['load', 'data', 'fetch', 'get', 'entity'],
			},
			{
				type: 'client_bulk_action' as const,
				label: 'Client Bulk Action',
				description:
					'Execute a bulk action on clients matching filter criteria',
				icon: faUsersGear,
				color: colors.values.primaryDark,
				keywords: [
					'bulk',
					'client',
					'action',
					'assign',
					'sales',
					'rep',
					'lead',
					'lifecycle',
					'group',
					'list',
				],
			},
		].filter(
			// PD-16917 — texting is the `sms` capability; the server skips the
			// step without it, so the builder does not offer it.
			(step) =>
				step.type !== 'send_sms' || CapabilityService.allows(Capabilities.SMS),
		),
		[colors.values],
	);

	// Filter step types based on search term
	const filteredStepTypes = useMemo(() => {
		if (searchTerm.trim() === '') {
			return availableStepTypes;
		}

		const lowerSearch = searchTerm.toLowerCase();

		return availableStepTypes.filter(
			(stepType) =>
				stepType.label.toLowerCase().includes(lowerSearch) ||
				stepType.description.toLowerCase().includes(lowerSearch) ||
				stepType.keywords.some((keyword) => keyword.includes(lowerSearch)),
		);
	}, [searchTerm, availableStepTypes]);

	// Reset state when modal opens/closes
	useEffect(() => {
		if (visible) {
			setSearchTerm('');
			setHighlightedIndex(0);

			// Focus search input when modal opens
			setTimeout(() => {
				searchInputRef.current?.focus();
			}, 100);
		}
	}, [visible]);

	// Reset highlighted index when filtered results change
	useEffect(() => {
		setHighlightedIndex(0);
	}, [filteredStepTypes.length]);

	// Handle keyboard navigation
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setHighlightedIndex((prev) =>
				prev < filteredStepTypes.length - 1 ? prev + 1 : prev,
			);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
		} else if (e.key === 'Enter') {
			e.preventDefault();

			if (filteredStepTypes[highlightedIndex] != null) {
				onSelect(filteredStepTypes[highlightedIndex].type);
				onCancel();
			}
		}
	};

	return (
		<Modal
			isOpen={visible}
			onClose={onCancel}
			title={t('Add Step')}
			className="max-w-[360px]"
		>
			<div className="space-y-3">
				{/* Search Input */}
				<div className="relative">
					<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
						<Icon
							icon={faSearch}
							className={`text-sm ${colors.classes.textSecondary}`}
						/>
					</div>
					<input
						ref={searchInputRef}
						type="text"
						value={searchTerm}
						onChange={(e) => {
							setSearchTerm(e.target.value);
						}}
						onKeyDown={handleKeyDown}
						placeholder={t('Search step types...')}
						className={`w-full pl-10 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 ${colors.classes.bgPrimary} ${colors.classes.borderDefault} ${colors.classes.textPrimary} focus:ring-accent`}
					/>
				</div>

				{/* Step Type List */}
				<div className="max-h-[300px] overflow-y-auto -mx-1 px-1">
					{filteredStepTypes.length === 0 ? (
						<div className={`py-6 text-center ${colors.classes.textSecondary}`}>
							{t('No step types match your search')}
						</div>
					) : (
						<div className="space-y-1">
							{filteredStepTypes.map((stepType, index) => (
								<button
									key={stepType.type}
									onClick={() => {
										onSelect(stepType.type);
										onCancel();
									}}
									onMouseEnter={() => {
										setHighlightedIndex(index);
									}}
									className={`w-full flex items-center px-3 py-2.5 rounded-lg transition-colors ${
										index === highlightedIndex
											? colors.classes.bgTertiary
											: `${colors.classes.bgPrimary} hover:${colors.classes.bgSecondary}`
									}`}
								>
									<div
										className="w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0"
										style={{ backgroundColor: `${stepType.color}15` }}
									>
										<Icon
											icon={stepType.icon}
											className="text-sm"
											style={{ color: stepType.color }}
										/>
									</div>

									<div className="flex-1 text-left min-w-0">
										<h4
											className={`text-sm font-medium ${colors.classes.textPrimary}`}
										>
											{t(stepType.label)}
										</h4>
										<p
											className={`text-xs ${colors.classes.textSecondary} truncate`}
										>
											{t(stepType.description)}
										</p>
									</div>
								</button>
							))}
						</div>
					)}
				</div>

				{/* Keyboard hint */}
				<div className={`text-xs ${colors.classes.textMuted} text-center pt-1`}>
					{t('↑↓ to navigate, Enter to select')}
				</div>
			</div>
		</Modal>
	);
};

export default StepSelectorModal;
